/**
 * ApprovalInboxCards.js — hai thẻ nhắc nhở dùng chung, cùng ăn 1 API
 * GET /approval-inbox/summary:
 *
 *   <PendingApprovalsCard />   — "ĐANG CHỜ BẠN DUYỆT" (cho trưởng phòng / người được
 *                                 uỷ quyền duyệt). Đặt trên Dashboard VÀ trang cá nhân,
 *                                 vì trưởng phòng "de-facto" (chức vụ trưởng phòng nhưng
 *                                 role EMPLOYEE) bị Dashboard đá về /portal — họ sẽ không
 *                                 bao giờ thấy nhắc nhở nếu chỉ gắn ở Dashboard.
 *   <MyPendingRequestsCard />  — "ĐƠN CỦA TÔI ĐANG CHỜ" (mọi nhân viên): gửi rồi mà lâu
 *                                 chưa ai trả lời thì phải thấy ngay ở trang của mình,
 *                                 kèm ĐANG NẰM Ở BÀN AI và CHỜ BAO LÂU.
 *
 * Cả hai tự ẩn khi không có gì → không chiếm chỗ của người không liên quan.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Inbox, Clock, AlertTriangle, ChevronRight, RefreshCw, Send, CheckCircle2,
} from 'lucide-react';
import api from '../../services/api';

/* Mức nhắc → màu. 'ok' = mới gửi, 'warn' = quá ngưỡng nhắc, 'overdue' = quá ngưỡng leo thang. */
const LEVEL = {
  ok: {
    chip: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    ring: 'border-blue-200 dark:border-blue-800',
    dot: 'bg-blue-500',
  },
  warn: {
    chip: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    ring: 'border-amber-300 dark:border-amber-700',
    dot: 'bg-amber-500',
  },
  overdue: {
    chip: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
    ring: 'border-rose-300 dark:border-rose-700',
    dot: 'bg-rose-500',
  },
};

/** "3 ngày 4 giờ" / "5 giờ" / "vừa gửi" — đọc nhanh hơn số giờ thô. */
export function fmtWaiting(hours) {
  const h = Number(hours) || 0;
  if (h < 1) return 'vừa gửi';
  if (h < 24) return `${Math.floor(h)} giờ`;
  const d = Math.floor(h / 24);
  const rem = Math.floor(h % 24);
  return rem > 0 ? `${d} ngày ${rem} giờ` : `${d} ngày`;
}

/** Hook dùng chung — 1 request cho cả 2 thẻ, tự làm mới mỗi 5 phút. */
export function useApprovalInbox({ auto = true, intervalMs = 5 * 60 * 1000 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/approval-inbox/summary', { params: { sample: 3 } });
      setData(res.data?.data || null);
    } catch (_) {
      setData(null); // im lặng: thẻ nhắc nhở không được phép làm hỏng trang chính
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (!auto) return undefined;
    const id = setInterval(load, intervalMs);
    return () => clearInterval(id);
  }, [load, auto, intervalMs]);

  return { data, loading, reload: load };
}

/* ── Thẻ 1: ĐANG CHỜ BẠN DUYỆT ────────────────────────────────────────────── */
export function PendingApprovalsCard({ inbox, loading, onReload, compact = false }) {
  const navigate = useNavigate();
  const a = inbox?.approver;
  if (loading || !a || !a.total) return null;

  const lv = LEVEL[a.level] || LEVEL.ok;
  const t = a.thresholds || {};

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border-2 ${lv.ring} overflow-hidden`}>
      <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${lv.chip}`}>
            <Inbox className="w-4.5 h-4.5" strokeWidth={2.2} />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
              Đang chờ bạn duyệt · {a.total} đơn
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
              {a.overdue > 0
                ? `${a.overdue} đơn QUÁ HẠN (trên ${t.escalate_hours}h) — đơn cũ nhất đã chờ ${fmtWaiting(a.oldest_hours)}`
                : a.warn > 0
                  ? `${a.warn} đơn chờ quá ${t.remind_hours}h — cũ nhất ${fmtWaiting(a.oldest_hours)}`
                  : `Đơn cũ nhất chờ ${fmtWaiting(a.oldest_hours)}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {onReload && (
            <button onClick={onReload} title="Làm mới"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          )}
          <button onClick={() => navigate('/approval-process')}
            className="text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-primary-600 text-white hover:bg-primary-700 inline-flex items-center gap-1">
            Xử lý <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {a.queues.map((q) => {
          const qlv = LEVEL[q.level] || LEVEL.ok;
          return (
            <button key={q.key} onClick={() => navigate(q.url)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition flex items-center gap-3">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${qlv.dot}`} />
              <span className="text-sm text-gray-800 dark:text-gray-100 font-medium flex-1 min-w-0 truncate">
                {q.label}
              </span>
              {q.overdue > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 flex items-center gap-0.5 flex-shrink-0">
                  <AlertTriangle className="w-3 h-3" /> {q.overdue} quá hạn
                </span>
              )}
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${qlv.chip}`}>
                {q.count}
              </span>
              <span className="text-[11px] text-gray-400 w-24 text-right flex-shrink-0 hidden sm:block">
                cũ nhất {fmtWaiting(q.oldest_hours)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Gợi ý cụ thể: 3 đơn kẹt lâu nhất, gọi thẳng tên người gửi — "nhắc" phải chỉ
          được đúng việc cần làm ngay, không chỉ là con số tổng. */}
      {!compact && (() => {
        const top = a.queues
          .flatMap((q) => (q.items || []).map((i) => ({ ...i, queue: q.label, url: q.url })))
          .sort((x, y) => y.waiting_hours - x.waiting_hours)
          .slice(0, 3)
          .filter((i) => i.level !== 'ok');
        if (!top.length) return null;
        return (
          <div className="px-4 py-2.5 bg-amber-50/60 dark:bg-amber-900/10 border-t border-amber-100 dark:border-amber-900/30">
            <p className="text-[11px] font-semibold text-amber-800 dark:text-amber-300 mb-1.5 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" /> Nên xử lý trước
            </p>
            <ul className="space-y-1">
              {top.map((i) => (
                <li key={`${i.queue}-${i.id}`}>
                  <button onClick={() => navigate(i.url)}
                    className="w-full text-left text-xs text-gray-700 dark:text-gray-200 hover:underline flex items-center gap-1.5">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${(LEVEL[i.level] || LEVEL.ok).dot}`} />
                    <b className="truncate">{i.employee_name}</b>
                    <span className="text-gray-500 truncate">— {i.title}</span>
                    {/* OT vọng gác bị CHẶN duyệt (guardDutyAlert.service). Không gắn nhãn thì
                        người duyệt bấm Duyệt, bị 409, rồi lặp lại mãi mà không hiểu vì sao. */}
                    {i.guard_blocked && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-600 text-white flex-shrink-0">
                        VỌNG GÁC — KHÔNG DUYỆT
                      </span>
                    )}
                    <span className="ml-auto text-gray-400 flex-shrink-0">chờ {fmtWaiting(i.waiting_hours)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })()}
    </div>
  );
}

/* ── Thẻ 2: ĐƠN CỦA TÔI ĐANG CHỜ ──────────────────────────────────────────── */
export function MyPendingRequestsCard({ inbox, loading, showWhenEmpty = false }) {
  const navigate = useNavigate();
  const m = inbox?.mine;
  if (loading || !m) return null;

  if (!m.count) {
    if (!showWhenEmpty) return null;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center gap-2.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Bạn không có đơn nào đang chờ duyệt.</p>
      </div>
    );
  }

  const lv = LEVEL[m.level] || LEVEL.ok;
  const t = m.thresholds || {};

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border-2 ${lv.ring} overflow-hidden`}>
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2.5">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${lv.chip}`}>
          <Send className="w-4 h-4" strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
            Đơn của tôi đang chờ duyệt · {m.count}
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {m.overdue > 0
              ? `${m.overdue} đơn đã quá ${t.escalate_hours}h chưa được trả lời — hãy nhắc người duyệt.`
              : m.warn > 0
                ? `${m.warn} đơn đã chờ quá ${t.remind_hours}h.`
                : 'Đơn đã gửi thành công, đang trong hàng chờ.'}
          </p>
        </div>
      </div>

      <ul className="divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
        {m.items.map((i) => {
          const ilv = LEVEL[i.level] || LEVEL.ok;
          return (
            <li key={`${i.kind}-${i.id}`}>
              <button onClick={() => navigate(i.url)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${ilv.chip}`}>{i.kind}</span>
                  <span className="text-sm text-gray-800 dark:text-gray-100 font-medium">{i.title}</span>
                  {i.level === 'overdue' && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300 inline-flex items-center gap-0.5">
                      <AlertTriangle className="w-3 h-3" /> Quá hạn trả lời
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  Đã chờ <b className={i.level === 'ok' ? '' : 'text-amber-700 dark:text-amber-300'}>{fmtWaiting(i.waiting_hours)}</b>
                  {i.current_approver_name
                    ? <>· đang ở bàn <b>{i.current_approver_name}</b></>
                    : <>· chờ người duyệt theo phạm vi phụ trách</>}
                </p>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default { PendingApprovalsCard, MyPendingRequestsCard, useApprovalInbox, fmtWaiting };
